/**
 * Weather Integration Agent
 * Provides weather forecasts for farm visits and optimal harvest predictions
 */

interface WeatherData {
  temperature: number;
  conditions: string;
  humidity: number;
  precipitation: number;
  windSpeed: number;
  uvIndex: number;
}

interface FarmVisitForecast {
  date: string;
  weather: WeatherData;
  recommendation: 'excellent' | 'good' | 'fair' | 'poor';
  reasons: string[];
  bestTimeToVisit: string;
}

interface HarvestPrediction {
  product: string;
  optimalHarvestDate: string;
  weatherConditions: WeatherData;
  qualityScore: number;
  notes: string[];
}

class WeatherIntegrationAgent {
  private weatherCache: Map<string, { data: any; timestamp: number }>;
  private cacheExpiration: number = 30 * 60 * 1000; // 30 minutes

  constructor() {
    this.weatherCache = new Map();
  }

  /**
   * Get weather forecast for a specific location
   * @param lat Latitude
   * @param lng Longitude
   * @param days Number of days to forecast (default 7)
   * @returns Weather forecast data
   */
  async getWeatherForecast(lat: number, lng: number, days: number = 7): Promise<WeatherData[]> {
    const cacheKey = `${lat},${lng},${days}`;
    const cached = this.weatherCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheExpiration) {
      return cached.data;
    }

    // Simulated weather data - In production, integrate with OpenWeatherMap or similar API
    const forecast: WeatherData[] = [];
    const baseTemp = 75;

    for (let i = 0; i < days; i++) {
      const dayOffset = i * 24 * 60 * 60 * 1000;
      const date = new Date(Date.now() + dayOffset);
      const tempVariation = Math.sin(i * 0.5) * 10;

      forecast.push({
        temperature: Math.round(baseTemp + tempVariation + Math.random() * 5 - 2.5),
        conditions: this.getRandomCondition(),
        humidity: Math.round(60 + Math.random() * 30),
        precipitation: Math.round(Math.random() * 30),
        windSpeed: Math.round(5 + Math.random() * 10),
        uvIndex: Math.round(5 + Math.random() * 5),
      });
    }

    this.weatherCache.set(cacheKey, { data: forecast, timestamp: Date.now() });
    return forecast;
  }

  /**
   * Get farm visit recommendation based on weather
   * @param lat Latitude
   * @param lng Longitude
   * @param visitDate Target visit date
   * @returns Visit recommendation with weather details
   */
  async getFarmVisitRecommendation(
    lat: number,
    lng: number,
    visitDate: Date = new Date()
  ): Promise<FarmVisitForecast> {
    const daysAhead = Math.floor((visitDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    const forecast = await this.getWeatherForecast(lat, lng, Math.max(daysAhead + 1, 7));
    const dayWeather = forecast[Math.min(daysAhead, 6)];

    const recommendation = this.calculateVisitRecommendation(dayWeather);
    const reasons = this.getVisitReasons(dayWeather);
    const bestTime = this.getBestVisitTime(dayWeather);

    return {
      date: visitDate.toISOString().split('T')[0],
      weather: dayWeather,
      recommendation,
      reasons,
      bestTimeToVisit: bestTime,
    };
  }

  /**
   * Predict optimal harvest timing based on weather patterns
   * @param product Product name
   * @param currentDate Current date
   * @returns Harvest prediction
   */
  async predictOptimalHarvest(product: string, currentDate: Date = new Date()): Promise<HarvestPrediction> {
    // Simplified harvest timing based on product and weather
    const harvestWindow = this.getHarvestWindow(product);
    const optimalDate = new Date(currentDate.getTime() + harvestWindow * 24 * 60 * 60 * 1000);

    // Get weather for optimal harvest date
    const forecast = await this.getWeatherForecast(26.6406, -81.8723, harvestWindow);
    const harvestDayWeather = forecast[Math.min(harvestWindow - 1, forecast.length - 1)];

    const qualityScore = this.calculateHarvestQuality(harvestDayWeather, product);
    const notes = this.getHarvestNotes(harvestDayWeather, product);

    return {
      product,
      optimalHarvestDate: optimalDate.toISOString().split('T')[0],
      weatherConditions: harvestDayWeather,
      qualityScore,
      notes,
    };
  }

  /**
   * Get weather impact on specific crops
   * @param products List of products
   * @param location Farm location
   * @returns Weather impact analysis
   */
  async getCropWeatherImpact(
    products: string[],
    location: { lat: number; lng: number }
  ): Promise<Map<string, { impact: 'positive' | 'neutral' | 'negative'; details: string }>> {
    const forecast = await this.getWeatherForecast(location.lat, location.lng, 7);
    const avgWeather = this.averageWeatherConditions(forecast);
    const impacts = new Map();

    for (const product of products) {
      const impact = this.assessCropImpact(product, avgWeather);
      impacts.set(product, impact);
    }

    return impacts;
  }

  // Helper methods

  private getRandomCondition(): string {
    const conditions = ['Sunny', 'Partly Cloudy', 'Cloudy', 'Light Rain', 'Rainy', 'Clear'];
    return conditions[Math.floor(Math.random() * conditions.length)];
  }

  private calculateVisitRecommendation(weather: WeatherData): 'excellent' | 'good' | 'fair' | 'poor' {
    let score = 100;

    // Temperature impact
    if (weather.temperature > 90 || weather.temperature < 50) score -= 30;
    else if (weather.temperature > 85 || weather.temperature < 60) score -= 15;

    // Precipitation impact
    if (weather.precipitation > 50) score -= 40;
    else if (weather.precipitation > 20) score -= 20;

    // Wind impact
    if (weather.windSpeed > 20) score -= 15;

    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'poor';
  }

  private getVisitReasons(weather: WeatherData): string[] {
    const reasons: string[] = [];

    if (weather.temperature >= 70 && weather.temperature <= 85) {
      reasons.push('Pleasant temperature for outdoor activities');
    }
    if (weather.precipitation < 20) {
      reasons.push('Low chance of rain');
    }
    if (weather.windSpeed < 10) {
      reasons.push('Calm wind conditions');
    }
    if (weather.uvIndex > 7) {
      reasons.push('High UV index - bring sun protection');
    }
    if (weather.temperature > 85) {
      reasons.push('Warm weather - stay hydrated');
    }

    return reasons;
  }

  private getBestVisitTime(weather: WeatherData): string {
    if (weather.temperature > 85) {
      return 'Early morning (7-10 AM) or late afternoon (4-6 PM) to avoid peak heat';
    }
    if (weather.uvIndex > 7) {
      return 'Late afternoon after 4 PM for lower sun exposure';
    }
    if (weather.precipitation > 20) {
      return 'Check weather updates before visiting';
    }
    return 'Anytime during business hours';
  }

  private getHarvestWindow(product: string): number {
    // Days until optimal harvest
    const windows: Record<string, number> = {
      tomatoes: 7,
      strawberries: 3,
      lettuce: 5,
      peppers: 10,
      cucumbers: 6,
      squash: 8,
      beans: 7,
      corn: 14,
    };

    return windows[product.toLowerCase()] || 7;
  }

  private calculateHarvestQuality(weather: WeatherData, product: string): number {
    let score = 100;

    // Ideal temperature range varies by crop
    const idealTemp = this.getIdealHarvestTemp(product);
    const tempDiff = Math.abs(weather.temperature - idealTemp);
    score -= tempDiff * 2;

    // Low precipitation is better for harvest
    if (weather.precipitation > 20) score -= 20;
    if (weather.precipitation > 50) score -= 30;

    // Moderate humidity is ideal
    if (weather.humidity < 40 || weather.humidity > 80) score -= 15;

    return Math.max(0, Math.min(100, score));
  }

  private getIdealHarvestTemp(product: string): number {
    const temps: Record<string, number> = {
      tomatoes: 75,
      strawberries: 65,
      lettuce: 60,
      peppers: 80,
      cucumbers: 70,
    };
    return temps[product.toLowerCase()] || 70;
  }

  private getHarvestNotes(weather: WeatherData, product: string): string[] {
    const notes: string[] = [];

    if (weather.precipitation > 30) {
      notes.push('Recent rain may affect field conditions - check with farm before visiting');
    }
    if (weather.temperature > 85) {
      notes.push('Warm weather - harvest early in morning for best quality');
    }
    if (weather.humidity > 80) {
      notes.push('High humidity may shorten shelf life - plan to use soon');
    }

    return notes;
  }

  private averageWeatherConditions(forecast: WeatherData[]): WeatherData {
    const avg = {
      temperature: 0,
      humidity: 0,
      precipitation: 0,
      windSpeed: 0,
      uvIndex: 0,
      conditions: forecast[0].conditions,
    };

    for (const day of forecast) {
      avg.temperature += day.temperature;
      avg.humidity += day.humidity;
      avg.precipitation += day.precipitation;
      avg.windSpeed += day.windSpeed;
      avg.uvIndex += day.uvIndex;
    }

    const count = forecast.length;
    avg.temperature = Math.round(avg.temperature / count);
    avg.humidity = Math.round(avg.humidity / count);
    avg.precipitation = Math.round(avg.precipitation / count);
    avg.windSpeed = Math.round(avg.windSpeed / count);
    avg.uvIndex = Math.round(avg.uvIndex / count);

    return avg;
  }

  private assessCropImpact(
    product: string,
    weather: WeatherData
  ): { impact: 'positive' | 'neutral' | 'negative'; details: string } {
    const idealTemp = this.getIdealHarvestTemp(product);
    const tempDiff = Math.abs(weather.temperature - idealTemp);

    if (tempDiff < 5 && weather.precipitation < 30) {
      return {
        impact: 'positive',
        details: 'Ideal weather conditions for growth and quality',
      };
    }

    if (tempDiff > 15 || weather.precipitation > 50) {
      return {
        impact: 'negative',
        details: 'Challenging weather conditions may affect availability',
      };
    }

    return {
      impact: 'neutral',
      details: 'Normal weather conditions expected',
    };
  }
}

// Export singleton instance
export const weatherIntegrationAgent = new WeatherIntegrationAgent();
export default weatherIntegrationAgent;
